import { computed, ref } from 'vue'

const VERSION_ENDPOINT = '/version.json'
const DEFAULT_CHECK_INTERVAL_MS = 60_000
const DISMISSED_VERSION_KEY = 'bpm_dismissed_update_version'

const currentVersion = ref<string | null>(null)
const latestVersion = ref<string | null>(null)
const updateAvailable = ref(false)

let updateCheckIntervalId: ReturnType<typeof setInterval> | null = null
let visibilityHandler: (() => void) | null = null
let isMonitorStarted = false

function getDismissedVersion(): string | null {
  return sessionStorage.getItem(DISMISSED_VERSION_KEY)
}

function setDismissedVersion(version: string | null): void {
  if (!version) {
    sessionStorage.removeItem(DISMISSED_VERSION_KEY)
    return
  }
  sessionStorage.setItem(DISMISSED_VERSION_KEY, version)
}

async function fetchVueVersion(): Promise<string | null> {
  try {
    const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(5000)
    })
    if (!response.ok) return null
    const json = await response.json()
    return typeof json?.vueVersion === 'string' ? json.vueVersion : null
  } catch {
    return null
  }
}

function setUpdateDetected(version: string | null): void {
  if (version) {
    latestVersion.value = version
    setDismissedVersion(null)
  }
  updateAvailable.value = true
}

export async function checkForAppUpdate(): Promise<void> {
  const remoteVersion = await fetchVueVersion()
  if (!remoteVersion) return

  if (!currentVersion.value) {
    currentVersion.value = remoteVersion
    latestVersion.value = remoteVersion
    return
  }

  latestVersion.value = remoteVersion
  if (remoteVersion !== currentVersion.value) {
    setUpdateDetected(remoteVersion)
  }
}

export function notifyAppUpdateFromRuntimeError(): void {
  setUpdateDetected(latestVersion.value)
}

export function dismissAppUpdatePrompt(): void {
  updateAvailable.value = false
  setDismissedVersion(latestVersion.value)
}

export function refreshAppNow(): void {
  window.location.reload()
}

export const showAppUpdatePrompt = computed(() => {
  if (!updateAvailable.value) return false
  const dismissedVersion = getDismissedVersion()
  if (!dismissedVersion) return true
  return dismissedVersion !== latestVersion.value
})

export function isLikelyStaleChunkError(error: unknown): boolean {
  const text = String((error as any)?.message || error || '').toLowerCase()
  return (
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('dynamically imported module') ||
    text.includes('loading chunk') ||
    text.includes('chunkloaderror') ||
    text.includes('importing a module script failed')
  )
}

export function reportStaleChunkError(error: unknown): void {
  if (!isLikelyStaleChunkError(error)) return
  notifyAppUpdateFromRuntimeError()
}

export async function startAppUpdateMonitor(intervalMs = DEFAULT_CHECK_INTERVAL_MS): Promise<void> {
  if (isMonitorStarted) return
  isMonitorStarted = true

  await checkForAppUpdate()

  updateCheckIntervalId = setInterval(() => {
    void checkForAppUpdate()
  }, intervalMs)

  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      void checkForAppUpdate()
    }
  }
  document.addEventListener('visibilitychange', visibilityHandler)
}

export function stopAppUpdateMonitor(): void {
  if (updateCheckIntervalId) {
    clearInterval(updateCheckIntervalId)
    updateCheckIntervalId = null
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler)
    visibilityHandler = null
  }
  isMonitorStarted = false
}
